/**
 * OnboardingPage.tsx
 * Three-step onboarding flow:
 *   1. CredentialsStep  — enter Network ID + API Key, validate
 *   2. ConfirmStep      — review network stats, confirm sync
 *   3. SyncStep         — Phase 1 progress, then "you're live" state
 *
 * API calls go through /api/validate-credentials (Worker).
 * Sync is triggered via /api/ingestion/start (Worker → Cloudflare Queue).
 */

import { useState } from "react";
import CredentialsStep from "./CredentialsStep";
import ConfirmStep from "./ConfirmStep";
import SyncStep from "./SyncStep";
import type { NetworkSummary } from "../../lib/types";

type Step = "credentials" | "confirm" | "syncing";

export default function OnboardingPage() {
  const [step, setStep] = useState<Step>("credentials");
  const [network, setNetwork] = useState<NetworkSummary | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  function handleValidated(result: NetworkSummary) {
    setNetwork(result);
    setStep("confirm");
  }

  function handleSyncStarted(id: string) {
    setJobId(id);
    setStep("syncing");
  }

  function handleReset() {
    setNetwork(null);
    setJobId(null);
    setStep("credentials");
  }

  return (
    <div className="onboarding-page">
      {step === "credentials" && (
        <CredentialsStep onValidated={handleValidated} />
      )}
      {step === "confirm" && network && (
        <ConfirmStep
          network={network}
          onSyncStarted={handleSyncStarted}
          onBack={handleReset}
        />
      )}
      {step === "syncing" && network && (
        <SyncStep
          network={network}
          jobId={jobId!}
        />
      )}
    </div>
  );
}
